<script setup lang="ts">
import SkeletonBlock from './SkeletonBlock.vue'

withDefaults(
  defineProps<{
    count?: number
    /** eval | case | agency | ai */
    variant?: 'eval' | 'case' | 'agency' | 'ai'
    showActions?: boolean
  }>(),
  {
    count: 5,
    variant: 'eval',
    showActions: true,
  },
)
</script>

<template>
  <div
    class="sk-card-list"
    :class="[`sk-card-list--${variant}`]"
    aria-busy="true"
    aria-label="列表加载中"
  >
    <template v-if="variant === 'agency'">
      <div class="sk-agency-grid">
        <div v-for="i in count" :key="i" class="sk-agency-card sk-surface">
          <SkeletonBlock variant="text" width="48px" height="20px" class="sk-agency-ribbon" />
          <div class="sk-agency-head">
            <SkeletonBlock variant="circle" width="48px" height="48px" />
            <div class="sk-agency-meta">
              <SkeletonBlock variant="title" width="78%" />
              <SkeletonBlock variant="text" width="52%" />
            </div>
          </div>
          <div class="sk-agency-lines">
            <SkeletonBlock
              v-for="l in 3"
              :key="l"
              variant="text"
              :width="l === 2 ? '65%' : '92%'"
            />
          </div>
          <div v-if="showActions" class="sk-card-actions">
            <SkeletonBlock v-for="b in 3" :key="b" variant="button" width="64px" inline />
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div
        v-for="i in count"
        :key="i"
        class="sk-eval-card sk-surface"
        :class="{ 'sk-eval-card--ai': variant === 'ai' }"
      >
        <div class="sk-eval-card__top">
          <div class="sk-eval-card__title-block">
            <SkeletonBlock variant="title" :width="variant === 'ai' ? '42%' : '55%'" />
            <SkeletonBlock variant="button" width="72px" height="24px" inline />
          </div>
          <SkeletonBlock variant="button" width="120px" height="40px" inline />
        </div>
        <div class="sk-eval-card__meta">
          <SkeletonBlock variant="text" width="38%" />
          <SkeletonBlock variant="text" width="34%" />
          <SkeletonBlock variant="text" width="28%" />
        </div>
        <SkeletonBlock variant="text" width="96%" />
        <SkeletonBlock variant="text" width="88%" />
        <div v-if="showActions" class="sk-card-actions">
          <SkeletonBlock
            v-for="b in variant === 'case' ? 6 : 3"
            :key="b"
            variant="button"
            width="68px"
            inline
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.sk-card-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sk-eval-card {
  padding: 16px 18px;
  border-left: 3px solid var(--sk-base);
}

.sk-eval-card--ai {
  padding: 18px 20px;
}

.sk-eval-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.sk-eval-card__title-block {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}

.sk-eval-card__meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 14px;
  margin: 12px 0 10px;
}

.sk-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.sk-agency-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.sk-agency-card {
  position: relative;
  padding: 16px;
  min-height: 200px;
}

.sk-agency-ribbon {
  position: absolute;
  top: 12px;
  right: 12px;
}

.sk-agency-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.sk-agency-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sk-agency-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

@media (max-width: 768px) {
  .sk-eval-card__meta {
    grid-template-columns: 1fr;
  }
}
</style>
